import { FileWithPath } from "@mantine/dropzone";
import { LLM } from "./models";

/** Raised when there is an error generating a single response from an LLM */
export class LLMResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LLMResponseError";
  }
}

// Dictionary types
export interface Dict<T = any> {
  [key: string]: T;
}

/** A string or a number representing the index to a hash table (`StringLookup`). */
export type StringOrHash = string | number;

export type ResponseUID = string;

/** What kind of data can be each individual response from the model.
 * string is basic text and number is a hash table index in the `StringLookup` table.
 * The object type is used for images and documents ("media") that are accessed via the `MediaLookup` table.
 * The `d` field is the unique ID in the `MediaLookup` table, NOT the raw data.
 */
export type LLMResponseData =
  | {
      t: "img" | "doc"; // type of Media
      d: string; // the unique ID in the MediaLookup table, NOT the raw data
    }
  | StringOrHash;

export function isImageResponseData(
  r: LLMResponseData,
): r is { t: "img"; d: string } {
  return typeof r === "object" && r.t === "img" && typeof r.d === "string";
}

// Function types
export type Func<T = void> = (...args: any[]) => T;

// JSON-compatible leaf types
export type JSONLeaf = string | number | boolean | null | undefined;
export type JSONCompatible<T = JSONLeaf> =
  | T
  | JSONCompatible<T>[]
  | Dict<JSONCompatible<T>>;

/** OpenAI function call format */
export interface OpenAIFunctionCall {
  name: string;
  parameters: Dict;
  description?: string;
}

// =================== Multimodal Chat Types ===================

/** ------ Anthropic chat message format */

export type ImageTypeAnthropic = string;
// | "image/jpeg"
// | "image/png"
// | "image/gif"
// | "image/webp";

export type ImageContentAnthropic =
  | { type: "base64"; media_type: ImageTypeAnthropic; data: string } // data: base64 encoded image
  | { type: "url"; url: string }; // url: image url

export type MultiModalContentAnthropic =
  | { type: "text"; text?: string; source?: never }
  | { type: "image"; source?: ImageContentAnthropic; text?: never };

/** ------ OpenAI chat message format */

export type ImageContentOpenAI = {
  url: string; // base64 encoded image OR image http-url
  detail?: "low" | "high" | "auto";
};

export type MultiModalContentOpenAI =
  | { type: "text"; text?: string; image_url?: never }
  | { type: "image_url"; image_url?: ImageContentOpenAI; text?: never };

/** ------ Gemini chat message format */

export type ImageContentGemini = {
  mimeType: string;
  // | "image/png"
  // | "image/jpeg"
  // | "image/webp"
  // | "image/heic"
  // | "image/heif";
  data: string; // base64 encoded image OR image http-url
};

export type MultiModalContentGemini =
  | { text: string }
  | { inlineData: ImageContentGemini };

// ===================

export interface ChatMessage {
  role: string;
  content: string;
  images?: string[]; // MediaLookup UIDs
  name?: string;
  function_call?: OpenAIFunctionCall;
  /** On an assistant turn: the model's own record of its reasoning, to send back to it in later turns (see extract_reasoning_state) */
  reasoning_state?: Dict;
}
export type ChatHistory = ChatMessage[];

export interface GeminiChatMessage {
  role: string;
  parts: [{ text: string }];
}

export interface GeminiChatContext {
  history: GeminiChatMessage[];
}

/** HuggingFace conversation models format */
export interface HuggingFaceChatHistory {
  past_user_inputs: string[];
  generated_responses: string[];
}

// Chat history with 'carried' variable metadata
export interface ChatHistoryInfo {
  messages: ChatHistory;
  fill_history: Dict<LLMResponseData>;
  metavars?: Dict<LLMResponseData>;
  llm?: string;
  uid?: ResponseUID;
}

export function isEqualChatHistory(
  A: ChatHistory | undefined,
  B: ChatHistory | undefined,
): boolean {
  if (A === undefined && B === undefined) return true;
  if (A === undefined || B === undefined) return false;
  if (A.length !== B.length) return false;
  if (A.length === 0) return true; // both empty
  return A.every((a, i) => {
    const b = B[i];
    return (
      a.role === b.role &&
      a.content === b.content &&
      a.name === b.name &&
      a.function_call === b.function_call
    );
  });
}

/** A standard async function interface for calling an LLM. */
export interface LLMAPICall {
  (
    prompt: string,
    model: LLM,
    n: number,
    temperature: number,
    params?: Dict,
    should_cancel?: () => boolean,
    images?: string[],
  ): Promise<[Dict, Dict]>;
}

export type QueryProgress = {
  success: number;
  error: number;
};

/** What LLM to call, at what settings. */
export type LLMSpec = {
  name: string;
  emoji: string;
  base_model: string;
  model: string;
  temp: number;
  key?: string;
  formData?: Dict<JSONCompatible>;
  settings?: Dict<JSONCompatible>;
  progress?: QueryProgress; // only used for front-end to display progress collecting responses for this LLM
};

export type LLMGroup = {
  group: string;
  emoji: string;
  items: LLMSpec[] | LLMGroup[];
};

/** A spec for a user-defined custom LLM provider */
export type CustomLLMProviderSpec = {
  name: string;
  emoji: string;
  models?: string[];
  rate_limit?: number | string;
  settings_schema?: {
    settings: Dict<Dict<JSONCompatible>>;
    ui: Dict<Dict<JSONCompatible>>;
  };
  category?: string;
};

/** Internal description of model settings, passed to react-json-schema */
export interface ModelSettingsDict {
  fullName: string;
  description?: string;
  schema: {
    type: "object";
    required: string[];
    properties: Dict<Dict<JSONCompatible>>;
    description?: string;
    /** JSON Schema dependencies, e.g. fields shown only for one choice. */
    dependencies?: Dict<any>;
  };
  uiSchema: Dict<JSONCompatible>;
  postprocessors: Dict<(val: string | number | boolean) => any>;
  /**
   * Defaults that differ by model, keyed by the value of the form's `model`
   * field, e.g. a higher token limit for models that reason before answering.
   * They take the place of the schema's defaults for that model.
   */
  modelDefaults?: Dict<Dict<JSONCompatible>>;
  /**
   * Options that differ by model: for a value of the form's `model` field, the
   * values each returned field offers, in place of its schema's enum (e.g. the
   * reasoning efforts a model accepts). See schemaForModel.
   */
  modelEnums?: (model: string) => Dict<JSONCompatible[]>;
}

/** Standard properties that every LLM response object must have. */
export interface BaseLLMResponseObject {
  /** A unique ID to refer to this response */
  uid: ResponseUID;
  /** The concrete prompt that led to this response. */
  prompt: StringOrHash;
  /** The variables fed into the prompt. */
  vars: Dict<LLMResponseData>;
  /** Any associated metavariables. */
  metavars: Dict<LLMResponseData>;
  /** The LLM to query (usually a dict of settings) */
  llm: StringOrHash | LLMSpec;
  /** Optional: The chat history to pass the LLM */
  chat_history?: ChatHistory;
}

/**
 * How long a response took, and how many tokens went in and came out, as far
 * as the provider reports. Every field is optional: providers report different
 * things, and responses cached before stats were recorded have none.
 */
export interface ResponseStats {
  /** Wall-clock time to get the response. Responses returned together by one request each get that request's time. */
  latency_ms?: number;
  /**
   * Time before the model started writing its output, as the server reports
   * it: loading the model if it wasn't loaded (Ollama) plus reading the prompt
   * (Ollama, llama.cpp, WebLLM). Only local servers report it. Named `ttft`
   * for time to first token, though it leaves out the network and the first
   * token itself.
   */
  ttft_ms?: number;
  /** Prompt tokens. When one request returned several responses, each gets the full prompt count. */
  input_tokens?: number;
  /** Generated tokens, including any reasoning tokens. */
  output_tokens?: number;
  /** Output tokens over latency, the same way for every provider, so models can be compared. */
  tokens_per_s?: number;
  /** How fast the model generated once it started, as measured by the server itself. Only local servers that report it (Ollama, llama.cpp, WebLLM). */
  decode_tokens_per_s?: number;
  /** What the request cost, in US dollars, where the provider reports it (e.g. OpenRouter's usage.cost). */
  cost_usd?: number;
  /**
   * The energy the request used in the provider's data centre, in Wh, as
   * EcoLogits estimates it from the model's size, the output tokens and the
   * latency (see backend/ecologits). A range, because model sizes and data
   * centres' overheads are often only known as one. Not measured.
   */
  est_energy_wh?: { min: number; max: number };
  /**
   * The energy the request used on this machine, in Wh, measured with its
   * energy counters: reading the prompt and generating, above the machine's
   * idle power. Only for local models on the machine running ChainForge.
   */
  energy_wh?: number;
  /** How far the usual swings in idle power could move `energy_wh`, in Wh. */
  energy_noise_wh?: number;
  /** `energy_wh` by part of the machine (e.g. gpu, cpu, dram), in Wh. */
  energy_parts_wh?: Dict<number>;
  /**
   * The energy, above idle, of loading the model before this request, in Wh,
   * shared with any requests that waited for the same load. Not in `energy_wh`.
   */
  load_energy_wh?: number;
  /** Set when this request's generation overlapped another's, which shared its energy. */
  energy_shared?: boolean;
  /**
   * What the machine ran the request under, which changes the energy the same
   * work takes: e.g. { power_source: "battery", power_mode: "Low Power",
   * thermal: "nominal" }. Low Power Mode, for one, runs the chip slower but
   * at a lower voltage, and so uses less energy per token.
   */
  energy_conditions?: Dict<string>;
  /** Set when the power source or mode changed during the request. */
  energy_conditions_changed?: boolean;
  /** Set when idle power was measured before the power settings last changed (no idle time since). */
  energy_baseline_before_change?: boolean;
  /**
   * Set when the server reported no timings (e.g. Ollama's decision models),
   * so `energy_wh` is over the whole request as ChainForge timed it, which
   * includes loading the model if it had to be loaded.
   */
  energy_includes_load?: boolean;
  /**
   * Set when the provider reported one total for several responses, e.g. one
   * request that returned n of them: how many responses the total was shared
   * between. The stats it covers (output tokens and speed, or latency) are
   * then that total's average.
   */
  averaged_over?: number;
}

/** A JSON object describing an LLM response for the same prompt, with n responses (n>=1) */
export interface RawLLMResponseObject extends BaseLLMResponseObject {
  // A snapshot of the exact query (payload) sent to the LLM's API
  // DEPRECATED: This is now deprecated since it wastes precious storage space.
  // query: Dict;
  // The raw JSON response from the LLM
  // NOTE: This is now deprecated since it wastes precious storage space.
  // raw_response: Dict;
  // Extracted responses (1 or more) from raw_response
  responses: LLMResponseData[];
  // Each response's reasoning (a reasoning model's "thinking"), in the same order as `responses`; null where there is none
  reasoning?: (StringOrHash | null)[];
  // Each response's reasoning state, which a Chat Turn sends back to the model (see extract_reasoning_state); null where there is none
  reasoning_state?: (Dict | null)[];
  // Each response's timing and token counts, in the same order as `responses`; null where there are none
  stats?: (ResponseStats | null)[];
  // Token lengths (if given)
  tokens?: Dict<number>;
}

export type EvaluationScore =
  | boolean
  | number
  | string
  | Dict<boolean | number | string>;

export type EvaluationResults = {
  items: EvaluationScore[];
  /**
   * The judge's probability for each score in `items`, where it gives one
   * (e.g. Jev): a number, or one per judge when scores are keyed by judge.
   * How likely the judge thinks its answer is right.
   */
  probs?: (number | Dict<number> | null)[];
  dtype:
    | "KeyValue"
    | "KeyValue_Numeric"
    | "KeyValue_Categorical"
    | "KeyValue_Mixed"
    | "Numeric"
    | "Categorical"
    | "Mixed"
    | "Unknown"
    | "Empty"
    | "Boolean";
};

/** A standard response format expected by the front-end. */
export interface LLMResponse extends BaseLLMResponseObject {
  // Extracted responses (1 or more) from raw_response
  responses: LLMResponseData[];
  // Each response's reasoning, in the same order as `responses`; null where there is none
  reasoning?: (StringOrHash | null)[];
  // Each response's reasoning state, in the same order as `responses`; null where there is none
  reasoning_state?: (Dict | null)[];
  // Each response's timing and token counts, in the same order as `responses`; null where there are none
  stats?: (ResponseStats | null)[];
  // Evaluation results
  eval_res?: EvaluationResults;
  // Token lengths (if given)
  tokens?: Dict<number>;
}

export type EvaluatedResponsesResults = {
  responses?: LLMResponse[];
  logs?: string[];
  error?: string;
};

/** The outputs of prompt nodes, text fields or other data passed internally in the front-end and to the PromptTemplate backend.
 * Used to populate prompt templates and carry variables/metavariables along the chain. */
export interface TemplateVarInfo {
  text?: StringOrHash;
  image?: string; // MediaLookup UID or a base64 image (NOTE: It may currently only use the UID.)
  fill_history?: Dict<LLMResponseData>;
  metavars?: Dict<LLMResponseData>;
  associate_id?: StringOrHash;
  prompt?: StringOrHash;
  uid?: ResponseUID;
  llm?: StringOrHash | LLMSpec;
  chat_history?: ChatHistory;
  reasoning_state?: Dict; // the response's reasoning state, for a Chat Turn to send back
}

export type LLMResponsesByVarDict = Dict<
  (BaseLLMResponseObject | LLMResponse | TemplateVarInfo | StringOrHash)[]
>;

export type VarsContext = {
  vars: string[];
  metavars: string[];
};

export type PromptVarType = LLMResponseData | TemplateVarInfo;
export type PromptVarsDict = {
  [key: string]: PromptVarType[] | LLMResponseData;
};

export type TabularDataRowType = Dict<StringOrHash>;
export type TabularDataColType = {
  key: string;
  header: string;
};

export type PythonInterpreter = "flask" | "pyodide";

export type RatingDict = Record<number, boolean | string | null | undefined>;

export interface FileWithContent extends FileWithPath {
  content?: string;
}
